import React, {MouseEvent, useState} from 'react';
import {navigate} from 'gatsby';
import styled from 'styled-components';
import {Formik, Field, FormikProps} from 'formik';
import {addMonths} from 'date-fns';
import {Box, breakpoint, Column, Columns} from '@nib/layout';
import validate from './validation';
import {Phone, Email, DateOfBirth, FunnelProvider, FUNNELS, Scale, CoverStartDate, YesNoRadio as VisitorTypeRadio, generateNameMask} from '@nib/form-fields';
import {PrimaryButton, SecondaryButton} from '@nib-components/button';
import {Scale as SCALE} from '@nib/phi-constants';
import {ChevronRightSystemIcon, DoctorNurseGraphicIcon, RewardsGraphicIcon, MobileDeviceGraphicIcon} from '@nib/icons';
import {FIELD_LABELS, FIELD_NAMES, VISITOR_TYPES} from './constants';
import {WelcomeDetailsForm, WelcomeDetailsValues} from './types';
import {FUNNEL_NAME, getBrand, isNibBrand, createServerDate} from '../../../services/utils';
import Heading from '@nib-components/heading';
import OnSubmitValidationErrorScrollTo from '../OnSubmitValidationErrorScrollTo';
import StyledForm from '../StyledForm';
import {saveFormOnBlur, saveFormOnDOBChange, saveFormOnChange, saveFormOnDateChange} from './eventHandlers';
import {connectValidationContext} from '../utils';
import {useValidationContext} from '../../ValidationContext';
import {useWelcomeStatic} from '../../../hooks/UseWelcomeStatic';
import {useHasMounted} from '../../../hooks/UseHasMounted';
import Expander from '@nib-components/expander';
import {Funds} from '@nib/phi-constants';
import CustomFirstName from './CustomFirstName';
import {nib} from '@nib-components/theme';
import Copy from '@nib-components/copy';
import Alert from '@nib-components/alert';
import {getCurrentDate} from '../../../utils';
import UtilityButton from '@nib/utility-button';
import {NIB_HOMEPAGE_URL} from '../../../constants';
import FeaturePanel from '@nib-components/feature-panel';
import PrivacyMessage from '../../PrivacyMessage';

const ContactHiddenCheckbox = styled(Field).attrs({type: 'checkbox'})`
  display: none;
  visibility: hidden;
`;

const WelcomeFeaturePanel = styled(FeaturePanel)`
  h3[data-mesh-component='HEADING'] {
    font-size: 1.4rem;
  }
`;

const FeatureColumn = styled(Column)`
  background-color: white;
  ${breakpoint('md')`
    min-height: calc(100vh - 54px - 254px);  //viewport height - header & footer height
  `};
`;

const StyledUtilityButton = styled(UtilityButton)`
  max-width: 540px;
`;
export const EXISTING_CUSTOMER_TYPE: keyof typeof VISITOR_TYPES = 'Customer';

export const CHANGE_COVER_LINK = 'https://go.nib.com.au/Y0LF/changecover';

const getHeadingStyles = (): {color: string; size: any} => {
  return isNibBrand() ? {color: nib.colors.brand.dark, size: {xs: 2, lg: 1}} : {color: '', size: {xs: 1}};
};

const toggleArhiWelcomeForm = (setShowArhiWelcomeForm: React.Dispatch<React.SetStateAction<'display' | 'none'>>) => () => {
  setShowArhiWelcomeForm((prev) => (prev === 'none' ? 'display' : 'none'));
};

const navigateToIwhi = () => navigate(`${NIB_HOMEPAGE_URL}/overseas-working-visitors`);

const navigateToIshi = () => navigate(`${NIB_HOMEPAGE_URL}/overseas-students`);

const handleMouseDown = (event: MouseEvent) => {
  if (event && event.target && event.type === 'mousedown') {
    const target = event.target;
    if (target instanceof HTMLElement && typeof target.click == 'function') {
      target.click();
    }
  }
};

const Component: React.FC<WelcomeDetailsForm> = (props) => {
  const {handleSubmit, updateSession, initialValues, showArhiPPCInformation} = props;
  const validationContext = useValidationContext();
  const {fieldLabels} = useWelcomeStatic();
  const todayAest = createServerDate(getCurrentDate());
  const monthFromToday = addMonths(todayAest, 1);
  const [showArhiWelcomeForm, setShowArhiWelcomeForm] = useState<'none' | 'display'>('none');
  const {color: headingColor, size: headingSize} = getHeadingStyles();
  const hasMounted = useHasMounted();

  if (!hasMounted) {
    return null;
  }

  return (
    <Box>
      <FunnelProvider value={{funnel: FUNNELS[FUNNEL_NAME]}}>
        <Columns collapseBelow="lg">
          <Column width="3/5">
            <Box margin={{xs: 6, lg: 6}} marginTop={{xs: 4, lg: 8}}>
              <Box marginTop={4} marginBottom={6}>
                <Heading component="h1" size={headingSize} color={headingColor}>
                  Get a quote
                </Heading>
                <Box marginTop={2}>
                  <Copy>By answering a few questions, you can find health insurance that fits your needs.</Copy>
                </Box>
              </Box>

              <Box marginBottom={6}>
                <PrivacyMessage />
              </Box>

              {showArhiWelcomeForm === 'none' && (
                <Box>
                  <Box marginBottom={4}>
                    <Heading component="h2" size={5} color={headingColor}>
                      What&apos;s your residency?
                    </Heading>
                  </Box>

                  <Box marginBottom={4}>
                    <StyledUtilityButton onClick={toggleArhiWelcomeForm(setShowArhiWelcomeForm)} variant="primary" size="small">
                      <UtilityButton.Title>Australian resident</UtilityButton.Title>
                      <UtilityButton.SubTitle>You are a citizen or permanent resident of Australia</UtilityButton.SubTitle>
                    </StyledUtilityButton>
                  </Box>
                  <Box marginBottom={4}>
                    <StyledUtilityButton onClick={navigateToIwhi} variant="primary" size="small">
                      <UtilityButton.Title>Overseas worker</UtilityButton.Title>
                      <UtilityButton.SubTitle>Working in Australia</UtilityButton.SubTitle>
                    </StyledUtilityButton>
                  </Box>
                  <Box marginBottom={4}>
                    <StyledUtilityButton onClick={navigateToIshi} variant="primary" size="small">
                      <UtilityButton.Title>Overseas student</UtilityButton.Title>
                      <UtilityButton.SubTitle>Studying in Australia</UtilityButton.SubTitle>
                    </StyledUtilityButton>
                  </Box>
                </Box>
              )}

              <Box display={showArhiWelcomeForm}>
                <Formik validate={validate(FUNNEL_NAME)} onSubmit={handleSubmit} initialValues={initialValues}>
                  {(formikBag: FormikProps<WelcomeDetailsValues>) => {
                    connectValidationContext(validationContext, formikBag);

                    return (
                      <>
                        <StyledForm name="welcome-form-info" id="welcome-form-info" onSubmit={formikBag.handleSubmit} onMouseDown={handleMouseDown} formType="optional">
                          <OnSubmitValidationErrorScrollTo />
                          <ContactHiddenCheckbox id="contact" name="contact" />

                          <Field
                            variant={getBrand()}
                            name={FIELD_NAMES.FIRST_NAME}
                            id={FIELD_NAMES.FIRST_NAME}
                            label={fieldLabels[FIELD_NAMES.FIRST_NAME]}
                            component={CustomFirstName}
                            maxLength={24}
                            mask={generateNameMask(24)}
                            onBlur={saveFormOnBlur(formikBag, updateSession)}
                          />

                          {formikBag.values.residentType === (Funds.ARHI || undefined) && (
                            <>
                              <Field
                                name={FIELD_NAMES.SCALE}
                                id={FIELD_NAMES.SCALE}
                                label={fieldLabels[FIELD_NAMES.SCALE]}
                                help=""
                                component={Scale}
                                onChange={saveFormOnChange(formikBag, updateSession)}
                              />
                              <Field
                                name={FIELD_NAMES.DATE_OF_BIRTH}
                                id={FIELD_NAMES.DATE_OF_BIRTH}
                                label={fieldLabels[FIELD_NAMES.DATE_OF_BIRTH]}
                                component={DateOfBirth}
                                onDateBlur={saveFormOnDOBChange(formikBag, updateSession)}
                              />
                              {[SCALE.Family, SCALE.Couple].includes(formikBag.values?.scale) && (
                                <Field
                                  name={FIELD_NAMES.PARTNER_DATE_OF_BIRTH}
                                  id={FIELD_NAMES.PARTNER_DATE_OF_BIRTH}
                                  label={fieldLabels[FIELD_NAMES.PARTNER_DATE_OF_BIRTH]}
                                  component={DateOfBirth}
                                  onDateBlur={saveFormOnDOBChange(formikBag, updateSession, FIELD_NAMES.PARTNER_DATE_OF_BIRTH)}
                                />
                              )}

                              {/* Optional field toggle-able by Optimizely - off by default */}
                              <Box display="none" data-exp-id="welcome-experiment-oba-email">
                                <Field name={FIELD_NAMES.EMAIL} id={FIELD_NAMES.EMAIL} label={fieldLabels[FIELD_NAMES.EMAIL]} component={Email} onBlur={saveFormOnBlur(formikBag, updateSession)} />
                              </Box>

                              {/* Optional field toggle-able by Optimizely - on by default */}
                              <Box display="inherit" data-exp-id="welcome-experiment-oba-phone">
                                <Field
                                  name={FIELD_NAMES.PHONE_NUMBER}
                                  id={FIELD_NAMES.PHONE_NUMBER}
                                  label={fieldLabels[FIELD_NAMES.PHONE_NUMBER]}
                                  component={Phone}
                                  maxLength={10}
                                  onBlur={saveFormOnBlur(formikBag, updateSession)}
                                />
                              </Box>

                              {showArhiPPCInformation && (
                                <Expander expanded={showArhiPPCInformation}>
                                  <Field
                                    name={FIELD_NAMES.COVER_START_DATE}
                                    id={FIELD_NAMES.COVER_START_DATE}
                                    component={CoverStartDate}
                                    months={1}
                                    minDate={todayAest}
                                    maxDate={monthFromToday}
                                    label={FIELD_LABELS.COVER_START_DATE}
                                    onChange={saveFormOnDateChange(formikBag, updateSession, FIELD_NAMES.COVER_START_DATE)}
                                  />
                                </Expander>
                              )}
                              {isNibBrand() && (
                                <>
                                  <Field
                                    component={VisitorTypeRadio}
                                    name={FIELD_NAMES.VISITOR_TYPE}
                                    id={FIELD_NAMES.VISITOR_TYPE}
                                    options={VISITOR_TYPES}
                                    label={FIELD_LABELS.VISITOR_TYPE}
                                    inline={false}
                                  />
                                  {formikBag.values?.visitorType === EXISTING_CUSTOMER_TYPE && (
                                    <Alert type="warning" variation="soft">
                                      If you are an nib member, you can login to your member account to compare or change your cover, update your excess, add Extras cover, add or remove people to your
                                      policy and more.
                                    </Alert>
                                  )}
                                </>
                              )}

                              <Box marginLeft={{xs: 0, lg: 0}}>
                                {/* Existing customers are prompted to the members login page or to "start new quote" */}
                                {formikBag.values?.visitorType === EXISTING_CUSTOMER_TYPE ? (
                                  <Box display={{xs: 'flex'}} flexWrap="wrap-reverse">
                                    <Box flexGrow={{xs: 1, md: 0}} marginTop={{xs: 4, md: 0}} marginRight={{xs: 0, md: 4}}>
                                      <PrimaryButton href={CHANGE_COVER_LINK} fullWidth={{xs: true, md: false}} icon={ChevronRightSystemIcon}>
                                        Login
                                      </PrimaryButton>
                                    </Box>
                                    <SecondaryButton fullWidth={{xs: true, md: false}} type="submit" isLoading={formikBag.isSubmitting} disabled={formikBag.isSubmitting}>
                                      Start a new quote
                                    </SecondaryButton>
                                  </Box>
                                ) : (
                                  <PrimaryButton fullWidth={{xs: true, md: false}} icon={ChevronRightSystemIcon} type="submit" isLoading={formikBag.isSubmitting} disabled={formikBag.isSubmitting}>
                                    Start
                                  </PrimaryButton>
                                )}
                              </Box>
                            </>
                          )}
                        </StyledForm>
                      </>
                    );
                  }}
                </Formik>
              </Box>
            </Box>
          </Column>
          <FeatureColumn width="2/5">
            <Box marginHorizontal={2} marginTop={3}>
              <WelcomeFeaturePanel title="Why choose nib for health insurance?" background="white" titleComponent="h3" columns={{xs: 1}}>
                <FeaturePanel.Feature icon={DoctorNurseGraphicIcon} title="Minimise out of pockets">
                  <Copy color="Darkest" align="center">
                    Our healthcare networks help you get the best value from your cover and lower out of pocket costs.
                  </Copy>
                </FeaturePanel.Feature>
                <FeaturePanel.Feature icon={RewardsGraphicIcon} title="Be rewarded for your membership">
                  <Copy color="Darkest" align="center">
                    Get everyday savings on transport, shopping and more with the nib Rewards program.
                  </Copy>
                </FeaturePanel.Feature>
                <FeaturePanel.Feature icon={MobileDeviceGraphicIcon} title="Your cover at your fingertips">
                  <Copy color="Darkest" align="center">
                    Manage your policy wherever you are with your member account and the nib App. Snap a photo of your receipt to claim, check your benefits on the go, and more!
                  </Copy>
                </FeaturePanel.Feature>
              </WelcomeFeaturePanel>
            </Box>
          </FeatureColumn>
        </Columns>
      </FunnelProvider>
    </Box>
  );
};

export default Component;
